import React from 'react';
import { Icon, RpgButton } from '../shared';
import { IngredientDetail, LogEntry } from '../../types';

interface LogDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    expiringIngredients: (IngredientDetail & { batches: any[] })[];
    onFindRecipe: (name: string) => void;
    logs?: LogEntry[];
    onOpenLogCenter?: () => void;
}

export const LogDetailModal = ({ isOpen, onClose, expiringIngredients, onFindRecipe, logs = [], onOpenLogCenter }: LogDetailModalProps) => {
    if (!isOpen) return null;

    const latestLogs = [...logs].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ).slice(0, 3);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-parchment dark:bg-parchment-dark w-full max-w-2xl rounded-xl border-4 border-wood-dark shadow-pixel p-1 jagged-corners animate-scale-in">
                <div className="border-2 border-[#deb887] dark:border-[#5d4037] rounded-lg p-6 bg-white dark:bg-[#2c2a25]">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-wood-light/30">
                        <h3 className="text-2xl font-black text-wood-darker dark:text-[#deb887] flex items-center gap-3">
                            <Icon name="history_edu" className="text-primary text-3xl" />
                            日志详情
                        </h3>
                        <RpgButton onClick={onClose} variant="neutral" className="size-10 !px-0 rounded">
                            <Icon name="close" />
                        </RpgButton>
                    </div>

                    {/* Content */}
                    <div className="space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2">
                        {/* Expiry Alerts Section */}
                        <div>
                            <h4 className="font-black text-lg text-wood-darker dark:text-[#deb887] mb-4 flex items-center gap-2">
                                <Icon name="warning" className="text-red-500" /> 过期提醒
                            </h4>

                            {expiringIngredients.length > 0 ? (
                                <div className="flex flex-col gap-3">
                                    {expiringIngredients.map((ingredient) => {
                                        const expiringBatch = ingredient.batches?.find(b => b.daysLeft < 3);
                                        const daysLeft = expiringBatch?.daysLeft || 0;
                                        const location = expiringBatch?.location || '冰箱';
                                        const quantity = expiringBatch?.quantity || 0;
                                        const unit = expiringBatch?.unit || ingredient.unit;

                                        return (
                                            <div
                                                key={ingredient.id}
                                                className="bg-red-50 dark:bg-red-900/10 border-2 border-red-500/30 rounded-lg p-4 shadow-sm flex items-center justify-between group hover:border-red-500 transition-colors"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="size-12 bg-white dark:bg-black/20 rounded border-2 border-red-200 flex items-center justify-center shrink-0">
                                                        <Icon name="warning" className="text-red-500 text-2xl animate-pulse" />
                                                    </div>
                                                    <div className="text-left flex-1">
                                                        <p className="text-base font-black text-red-800 dark:text-red-200 leading-tight">
                                                            你【{location}】里的【{quantity}{unit}】【{ingredient.name}】还有【{daysLeft}】天过期
                                                        </p>
                                                        <p className="text-xs font-bold text-red-600/70 dark:text-red-400/70 mt-1">
                                                            建议尽快处理以避免浪费
                                                        </p>
                                                    </div>
                                                </div>
                                                <RpgButton
                                                    onClick={() => onFindRecipe(ingredient.name)}
                                                    variant="danger"
                                                    className="text-xs font-black px-4 py-2 ml-4"
                                                >
                                                    去消耗
                                                </RpgButton>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="bg-green-50 dark:bg-green-900/10 border-2 border-green-500/30 rounded-lg p-5 text-center flex items-center justify-center gap-3">
                                    <Icon name="check_circle" className="text-green-500 text-xl" />
                                    <p className="text-green-800 dark:text-green-200 font-bold">目前没有即将过期的食材</p>
                                </div>
                            )}
                        </div>

                        {/* Logs Section */}
                        <div className="border-t-2 border-dashed border-wood-light/30 pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h4 className="font-black text-lg text-wood-darker dark:text-[#deb887] flex items-center gap-2">
                                    <Icon name="event_note" className="text-primary" /> 最近记录
                                </h4>
                                {onOpenLogCenter && (
                                    <button onClick={() => { onClose(); onOpenLogCenter(); }} className="text-xs text-primary font-black hover:underline">
                                        查看全部
                                    </button>
                                )}
                            </div>

                            {latestLogs.length > 0 ? (
                                <div className="space-y-3">
                                    {latestLogs.map(log => (
                                        <div key={log.id} className="bg-parchment/50 dark:bg-parchment-dark/50 p-4 border-2 border-wood-light/20 rounded-lg">
                                            <div className="flex justify-between items-start mb-1 text-[10px] opacity-50 font-mono">
                                                <span>笔记</span>
                                                <span>{new Date(log.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <p className="text-sm text-wood-dark dark:text-[#deb887] italic line-clamp-3">
                                                “{log.content}”
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 border-2 border-dashed border-wood-light/20 rounded-lg">
                                    <p className="text-sm text-wood-light dark:text-wood-dark font-bold">还没有留下任何记录哦</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 flex justify-between gap-4">
                        <RpgButton
                            onClick={() => {
                                onClose();
                                onOpenLogCenter?.();
                            }}
                            variant="success"
                            className="flex-1 font-black"
                        >
                            前往日志中心
                        </RpgButton>
                        <RpgButton onClick={onClose} variant="neutral" className="px-8 font-black">
                            关闭
                        </RpgButton>
                    </div>
                </div>
            </div>
        </div>
    );
};
